import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import { MatchesSortBy } from 'constants/enums/matches-sort-by.enum';
import { biopetUpdateCardQuery, biopetUpdateCardQueryVariables } from 'constants/query/biopetUpdateCardQuery';
import { registrationConsentsQuery, registrationMarketingConsentsQuery } from 'constants/query/consentsQuery';
import { GET_COOKIES_DISCLOSURE } from 'constants/query/cookiesDisclosure';
import { GET_DASHBOARD_HEALTH_REPORT_QUERY } from 'constants/query/dashboardHealthReportQuery';
import { GET_FOOTER_QUERY } from 'constants/query/footerQuery';
import { GET_NAV_BAR_QUERY } from 'constants/query/headerQuery';
import { HEALTH_TRAIT_BODY_FUNCTIONS_QUERY } from 'constants/query/healthTraitBodyFunctionsQuery';
import {
  getHealthTraitInfoListQueryForFilters,
  getHealthTraitInfoListQueryPDFData,
  getHealthTraitInheritanceResults,
  getHealthTraitsForInheritance,
  HEALTH_TRAIT_INFO_QUERY,
} from 'constants/query/healthTraitInfoQuery';
import { HEALTH_TRAIT_RESULT_TYPES_QUERY } from 'constants/query/healthTraitResultTypeQuery';
import { KIT_PRICES_ONLY_QUERY } from 'constants/query/kitPricesQuery';
import { GET_LOGGED_IN_DASHBOARD_QUERY } from 'constants/query/loggedInDashboardQuery';
import { GET_OFFER_QUERY } from 'constants/query/offerQuery';
import { PAGE_CO_BRANDED_QUERY } from 'constants/query/pageCoBrandedQuery';
import { PAGE_HOME_QUERY } from 'constants/query/pageHomeQuery';
import { PAGE_PARTNERSHIPS_QUERY } from 'constants/query/pagePartnershipsQuery';
import { PAGE_REDIRECT_FROM_PETDNA_QUERY } from 'constants/query/pageRedirectFromPetDNAQuery';
import { SURVEY_QUERY, SURVEYS_QUERY } from 'constants/query/pageSurveyQuery';
import { GET_POLICY_QUERY } from 'constants/query/policyQuery';
import { getPopupListQuery } from 'constants/query/popups/popupListQuery';
import { POPUPS_QUERY } from 'constants/query/popups/popupQuery';
import {
  SECTION_DASHBOARD_CAROUSEL_QUERY,
} from 'constants/query/sections/sectionDashboardCarouselQuery';
import {
  SECTION_DASHBOARD_PARTNERSHIP_QUERY,
} from 'constants/query/sections/sectionDashboardPartnershipQuery';
import { getShareSheetTextQuery } from 'constants/query/shareSheetTextQuery';
import { socialShareButtonListQuery } from 'constants/query/socialShare/socialShareButtonsQuery';
import { IBiopetUpdateCardQuery } from 'constants/types/biopetUpdateCard';
import { PageOfferVars } from 'constants/types/pageOffer';
import { getDomain } from 'helpers/getDomain';
import { ISocialShareButtonList } from 'hooks/socialShare/type';
import { HealthTraitPrediction } from 'hooks/useHealthTraitRecords';
import { TSupportedCountryCodes, TSupportedLocales } from 'petdna-lib-uicomponents/constants';
import { getLocalesByCountryCode, loadAllChunks } from 'petdna-lib-uicomponents/helpers';
import {
  breedQuery,
  IKitPrices,
  IShoppingCart,
  IShoppingCartVariables,
  ISiteFooter,
  ISiteFooterVariables,
  ISiteHeader,
  ISiteHeaderVariables,
  IUnionPage, IUnionPageVars, PROMO_BANNER_QUERY, shoppingCartQuery, siteFooterQuery,
  siteHeaderQuery,
  TForProject,
  unionPagesQuery,
} from 'petdna-lib-uicomponents/hygraph';
import { ERedirectType, ITopPagePromoBanner } from 'petdna-lib-uicomponents/types';
import {
  BreedNames, BreedResults, BreedsBasicInfo,
} from 'stores/types/Breeds';
import { IFaqSearchResultItem } from 'stores/types/FAQs';
import {
  IDashboardHealthReport,
  IGetHealthReportStatus,
  IGetHealthSummaryReport,
  IGetHealthSummaryReportResponse,
  IHealthReportResponse,
} from 'stores/types/Health';
import {
  AllMatchesResponse, AllPawfectMatchesResponse, MatchGroups, PawfectMatchesData,
} from 'stores/types/Matches';
import {
  ICard,
  ICookieDisclosure,
  IPageHome,
  IPageLoggedInDashboard,
  IPageOffer,
  IPagePartnerships,
  TFooter,
  TNavbar,
  TPageCoBrandedSections,
  TPageRedirectFromPetDna,
  TPolicyPage,
} from 'stores/types/Pages';
import {
  IPopup,
  IPopupList,
  TPopupSetting,
} from 'stores/types/Popups';
import {
  AddressInfo,
  AddressResponse,
  ImageToken,
  Profile,
  ProfileResults,
  RecollectResults,
  TUpdateUserProfile,
  UserProfile,
} from 'stores/types/Profiles';
import {
  IQualtricsSession,
  ISaveQualtricsAnswerResponse,
  IStartQualtricsSessionResponse,
  TQualtricsSessionResponse,
} from 'stores/types/Qualtrics';
import { ISectionDashboardCarousel, ISectionDashboardPartnership } from 'stores/types/Sections';
import { SessionValid } from 'stores/types/Session';
import { ISurvey, ISurveys } from 'stores/types/Survey';
import {
  IGenerateShareImage,
  IShipmentResponse,
  RegistrationInfo, ShareSheetText, TestDownloadResponse,
  TestHistory, TTestInfo,
} from 'stores/types/Tests';
import {
  HealthTraitBodyFunction,
  HealthTraitInfo,
  HealthTraitInfoFilters,
  HealthTraitInheritance,
  HealthTraitInheritanceResult,
  HealthTraitResultType,
  TraitCategory,
  TraitInfo,
  TraitsBasicInfo,
  TraitsResults,
} from 'stores/types/Traits';

const headers = {
  accept: 'application/json',
  'Content-Type': 'application/json',
};

const hygraphHeader = {
  Authorization: `Bearer ${process.env.REACT_APP_HYGRAPH_API_KEY}`,
  'Content-Type': 'application/json; charset=utf-8',
};

const defaultUndeterminedHealthTraitResultType: HealthTraitResultType = {
  title: 'Undetermined',
  titleShort: 'Undetermined',
  traitPredictionId: HealthTraitPrediction.UNDETERMINED,
  riskDescription: {
    text: 'Some Description',
    html: 'Some Description',
  },
  riskDescriptionTitle: {
    text: 'What does Undetermined result mean?',
    html: 'What does Undetermined result mean?',
  },
  icon: {
    width: 0,
    height: 0,
    url: 'https://us-east-1-shared-usea1-02.graphassets.com/A6tNDLeCTUirSLNvUSdtAz/cmdeozlgxao1o07lgczaliup1',
  },
  affectedBreedsBackgroundColor: {
    hex: '#E1FF8B',
  },
  breedsAccordionBackgroundColor: {
    hex: '#E1FF8B',
  },
  backgroundColor: {
    hex: '#ECE9E4',
  },
  textColor: {
    hex: '#2F2F2E',
  },
};

const defaultUndeterminedHealthTraitInheritanceResult: HealthTraitInheritanceResult = {
  inheritance: HealthTraitInheritance.UNDETERMINED,
  predictionType: defaultUndeterminedHealthTraitResultType,
  displayType: defaultUndeterminedHealthTraitResultType,
  hasMatchingBreeds: true,
  title: {
    html: 'It is unknown if {{ petProfile.name }} is carrying the affected gene.',
    text: 'It is unknown if {{ petProfile.name }} is carrying the affected gene.',
  },
  description: {
    html: '{{ petProfile.name }}\'s genetic test does not show any information about carrying the affected gene.',
    text: '{{ petProfile.name }}\'s genetic test does not show any information about carrying the affected gene.',
  },
};

const handleErrorRedirect = (
  error: { response: { status: number, data: { [key: string]: unknown, error: object } } },
) => {
  if (!error.response) return Promise.reject(error);
  if (error.response.status === 401) {
    return window.location.replace(
      `${window.location.origin}/mypets/sign-in?returnUrl=${window.location.href}`,
    );
  }
  if (error.response.status === 400) {
    return Promise.reject(error.response);
  }
  if (error.response.status === 500) {
    return Promise.reject(error.response);
  }
  if (error.response.status === 502) {
    return Promise.reject(error.response);
  }
  if (error.response.status === 503) {
    if (error.response.data.error) return error.response;

    return null;
  }
  // if (error.response.status >= 400 && error.response.status < 500) {
  //   return window.location.replace(`${window.location.origin}/404`);
  // }
  return null;
};

const initApiClient = () => {
  const domain = getDomain();
  const hygraphUrl = new URL(process.env.REACT_APP_GRAPH_QL_READ_CONTENT_API ?? '');
  const baseURL = window.location.origin === 'http://localhost:3000'
    ? origin
    : `${origin}/`;
  const hygraphBaseURL = window.location.origin === 'http://localhost:3000' || window.location.origin.includes('dnalocal.')
    ? origin
    : hygraphUrl.origin;

  const hygraphApiEndpoint = hygraphUrl.pathname;

  const mapboxBaseURL = window.location.origin.includes('localhost') || window.location.origin.includes('dnalocal.')
    ? origin
    : 'https://api.mapbox.com/';
  const mediaUploadBaseURL = window.location.origin.includes('localhost') || window.location.origin.includes('dnalocal.')
    ? origin
    : `https://mediaupload.${domain}`;

  const axiosInstance = axios.create({
    withCredentials: true,
    baseURL,
    headers,
    paramsSerializer: {
      indexes: null,
    },
  });

  const hygraphAxiosInstance = axios.create({
    baseURL: hygraphBaseURL,
    headers: hygraphHeader,
  });

  const mapboxAxiosInstance = axios.create({
    baseURL: mapboxBaseURL,
    headers,
  });

  const mediaUploadAxiosInstance = axios.create({
    baseURL: mediaUploadBaseURL,
    headers,
  });

  axiosRetry(axiosInstance, {
    retries: 3,
    retryDelay: exponentialDelay,
    retryCondition: axiosRetry.isRetryableError,
  });

  axiosInstance.interceptors.response.use((resp) => resp, handleErrorRedirect);

  return {
    amity: {
      getAmityAuthToken: () => axiosInstance.get('api/amity/tokens'),
      getAmityChannel: (testGuid: string, toTestGuid: string) => {
        const params = [];
        if (toTestGuid) {
          params.push(`toTestGuid=${toTestGuid}`);
        }
        const paramStr = params.length > 0 ? `?${params.join('&')}` : '';
        return axiosInstance.get(`api/profiles/${testGuid}/channel${paramStr}`);
      },
      getHasAnyChannel: () => axiosInstance.get<{ hasChannel: boolean, allDisabled: boolean }>('api/profiles/has-any-channel'),
    },
    breeds: {
      getBreedResults: (testId: string) => axiosInstance.get<BreedResults>(
        `/api/tests/${testId}/breeds`,
      ),
      getAllBreedsInfo: () => hygraphAxiosInstance.post(hygraphApiEndpoint, JSON.stringify({
        query: 'query BreedsQuery{breeds(first:1000){breedID name showDetails height{maximum minimum}weight{maximum minimum}lifespan{maximum minimum}overview origin funFact traits today photo{url}exercise nutrition grooming training}}',
        variables: {},
      })),
      getAllBreedNames: (skip: number) => hygraphAxiosInstance.post<{ data: BreedNames }>(
        hygraphApiEndpoint,
        JSON.stringify({
          query: 'query BreedsQuery($skipNum:Int){breeds(first:1000,skip:$skipNum){breedID name}breedsConnection{aggregate{count}}}',
          variables: {
            skipNum: skip,
          },
        }),
      ),
      getBreedsList: (breedIDs: string[]) => hygraphAxiosInstance.post<{ data: BreedsBasicInfo }>(
        hygraphApiEndpoint,
        JSON.stringify({
          query: 'query BreedsQuery($breedIDs:[String]){breeds(first:1000,where:{breedID_in:$breedIDs}){breedID name overview originCountryCode photo{url} showDetails}}',
          variables: {
            breedIDs,
          },
        }),
      ),
      getBreedInfo: (breedID: string) => hygraphAxiosInstance.post(
        hygraphApiEndpoint,
        JSON.stringify({
          query: breedQuery,
          variables: {
            where: { breedID },
          },
        }),
      ),
    },

    mapbox: {
      getLocationSuggestions: (
        q: string,
        sessionToken: string,
        options: string,
      ) => mapboxAxiosInstance.get<any>(
        `/search/searchbox/v1/suggest?q=${q}&access_token=pk.eyJ1IjoiYW5jZXN0cnltYXBib3giLCJhIjoiNllqcGhKYyJ9.p9QKjx4kc2E_55jLTmDw0Q&session_token=${sessionToken}&${options}`,
      ),
      retrieveLocationSuggestions: (
        id: string,
        sessionToken: string,
      ) => mapboxAxiosInstance.get<any>(
        `/search/searchbox/v1/retrieve/${id}?access_token=pk.eyJ1IjoiYW5jZXN0cnltYXBib3giLCJhIjoiNllqcGhKYyJ9.p9QKjx4kc2E_55jLTmDw0Q&session_token=${sessionToken}`,
      ),
      getCategoryLocations: (category: string, options: string) => mapboxAxiosInstance.get<any>(
        `/search/searchbox/v1/category/${category}?access_token=pk.eyJ1IjoiYW5jZXN0cnltYXBib3giLCJhIjoiNllqcGhKYyJ9.p9QKjx4kc2E_55jLTmDw0Q&${options}`,
      ),
    },

    matches: {
      getAllMatches: (page = 1, pageSize = 5, excludeWithChannel = true) => {
        const params = new URLSearchParams({
          page: page.toString(),
          excludeWithChannel: JSON.stringify(excludeWithChannel),
          page_size: pageSize.toString(),
        });

        return axiosInstance.get<AllMatchesResponse>(
          `/api/tests/all/matches?${params.toString()}`,
        );
      },
      getAllPawfectMatches: (page = 1, pageSize = 5, excludeWithChannel = true) => {
        const params = new URLSearchParams({
          page: page.toString(),
          excludeWithChannel: JSON.stringify(excludeWithChannel),
          page_size: pageSize.toString(),
        });

        return axiosInstance.get<AllPawfectMatchesResponse>(
          `/api/tests/all/pawfect-matches?${params.toString()}`,
        );
      },
      getMatches: (
        testId: string,
        q?: string,
        sortby = MatchesSortBy.RELATIONSHIP,
        starred = false,
        page = 1,
        pageSize = 5,
      ) => axiosInstance.get<MatchGroups>(
        `/api/tests/${testId}/matches`,
        {
          params: {
            q,
            sortby,
            starred: starred.toString(),
            page: page.toString(),
            page_size: pageSize.toString(),
          },
        },
      ),
      addFavoriteMatch: (testId: string, matchTestId: string) => axiosInstance.put(
        `/api/tests/${testId}/matches/favorites?favorite=${matchTestId}`,
      ),
      removeFavoriteMatch: (testId: string, matchTestId: string) => axiosInstance.delete(
        `/api/tests/${testId}/matches/favorites?favorite=${matchTestId}`,
      ),
      getPawfectMatches: (
        testId: string,
        q?: string,
        starred = false,
        page: number = 1,
        pageSize: number = 5,
      ) => axiosInstance.get<PawfectMatchesData>(
        `/api/tests/${testId}/pawfectmatches`,
        {
          params: {
            q,
            starred: starred.toString(),
            page: page.toString(),
            page_size: pageSize.toString(),
          },
        },
      ),
    },

    session: {
      isSessionValid: () => axiosInstance.get<SessionValid>(
        '/api/authn/user-session-valid',
      ),
      signOut: () => axiosInstance.get<any>(
        '/api/auth/signout',
      ),
    },

    tests: {
      downloadInfo: (testId: string) => axiosInstance.get<TestDownloadResponse>(
        `api/profiles/${testId}/download`,
      ),
      getTestHistory: (testId: string) => axiosInstance.get<TestHistory>(
        `/api/bff/tests/${testId}/history`,
      ),
      getTests: () => axiosInstance.get<TTestInfo[]>(
        '/api/bff/tests',
      ),
      registerTest:
        (registrationInfo: RegistrationInfo) => axiosInstance.post<{
          status: string; testGUID: string
        }>(
          '/api/registrations',
          { registration: registrationInfo },
        ),
      registerTestWithRecollect:
        (registrationInfo: RegistrationInfo, testGUID: string) => axiosInstance.post<{
          status: string; testGUID: string
        }>(
          `/api/registrations/${testGUID}`,
          { registration: registrationInfo },
        ),
      getReturnLabel:
        (testId: string, config: AxiosRequestConfig) => axiosInstance.get<IShipmentResponse>(`/api/bff/tests/${testId}/shipment`, config),
      getPetProfile: (testId: string) => axiosInstance.get<ProfileResults>(
        `/api/profiles/${testId}`,
      ),
      getPetProfiles: () => axiosInstance.get<ProfileResults>(
        '/api/profiles/',
      ),
      getUserAddresses: (testId: string) => axiosInstance.get<AddressResponse>(
        '/api/profiles/addresses',
        { params: { testGuid: testId } },
      ),
      createAddress: (address: AddressInfo) => axiosInstance.post<{ addressId: string }>(
        '/api/profiles/addresses',
        { address },
      ),
      recollectAccept:
        (orderAddressId: string, testId: string) => axiosInstance.post<RecollectResults>(
          `/api/registrations/recollect/${testId}`,
          { recollect: { orderAddressId } },
        ),
      updatePetProfile:
        (testId: string, profile: Partial<Profile>) => axiosInstance.patch<{ profile: Profile }>(
          `/api/profiles/${testId}`,
          { profile },
        ),
      deletePetProfile:
        (testId: string) => axiosInstance.delete<{ profile: Profile }>(
          `/api/profiles/${testId}`,
        ),
      getShareSheetText:
        (id: string) => hygraphAxiosInstance.post<{ data: ShareSheetText }>(
          hygraphApiEndpoint,
          JSON.stringify({ query: getShareSheetTextQuery(id) }),
        ),
      generateShareImage:
        (testGuid: string, payload: IGenerateShareImage) => axiosInstance.post(`/api/share/${testGuid}`, payload),
      updateMessagesReceiveStatus:
        (
          testId: string,
          profile: Profile,
        ) => axiosInstance.patch<{ profile: Profile }>(
          `/api/profiles/${testId}`,
          { profile },
        ),
      getPetProfilesByChannels:
        (channelIds: URLSearchParams) => axiosInstance.get<{ profiles: Array<{ name: string, channelId: string }> }>('/api/profiles/by-chat', {
          params: channelIds,
        }),
    },

    healthTraits: {
      getHealthReport: (
        testId: string,
      ) => axiosInstance.get<IHealthReportResponse>(
        `api/tests/${testId}/health-report`,
      ),
      getHealthSummaryReport: (
        testGuid: string,
        payload: IGetHealthSummaryReport,
        controller?: AbortController,
      ) => axiosInstance.post<IGetHealthSummaryReportResponse>(
        `api/tests/${testGuid}/health-report/summary-pdf`,
        payload,
        controller ? { signal: controller.signal } : undefined,
      ),
      getReportStatus: (
        testGuid: string,
        id: string,
        controller?: AbortController,
      ) => axiosInstance.get<IGetHealthReportStatus>(
        `api/tests/${testGuid}/health-report/summary-pdf/${id}`,
        controller ? { signal: controller.signal } : undefined,
      ),
      getDashboardHealthReport: (
        kitsSaleCountry: TSupportedCountryCodes,
      ) => hygraphAxiosInstance.post<{
        data: { dashboardHealthReport: IDashboardHealthReport }
      }>(
        hygraphApiEndpoint,
        JSON.stringify(
          { query: GET_DASHBOARD_HEALTH_REPORT_QUERY, variables: { kitsSaleCountry } },
        ),
      ),
      getEligibility: (
        testId: string,
        offerId: string,
      ) => axiosInstance.get<{ eligible: boolean }>(
        `api/tests/${testId}/health-traits-eligibility/${offerId}`,
      ),
      getHealthTraitInfo:
        (
          traitId: string,
          countryCode: TSupportedCountryCodes,
          locale: TSupportedLocales[],
        ) => hygraphAxiosInstance.post<
        { data: { healthTraits: HealthTraitInfo[] } }
        >(
          hygraphApiEndpoint,
          JSON.stringify({
            query: HEALTH_TRAIT_INFO_QUERY,
            variables: {
              traitId,
              countryCode: [countryCode],
              locale,
            },
          }),
        ),
      listHealthTraitInfo:
        async (filters: HealthTraitInfoFilters) => {
          const chunks = await loadAllChunks(
            100,
            (params) => hygraphAxiosInstance.post<
            { data: { healthTraits: HealthTraitInfo[] } }
            >(
              hygraphApiEndpoint,
              JSON.stringify({
                query: getHealthTraitInfoListQueryForFilters(filters, params),
                variables: filters,
              }),
            ),
            (response) => response.data.data.healthTraits.length,
          );

          return chunks.reduce((acc, chunk) => {
            acc.data.data.healthTraits = acc.data
              .data.healthTraits.concat(chunk.data.data.healthTraits);
            return acc;
          }, {
            data: {
              data: {
                healthTraits: [],
              },
            },
          } as AxiosResponse<{ data: { healthTraits: [] } }>);
        },
      listHealthTraitInfoPDFData:
        async () => {
          const chunks = await loadAllChunks(
            100,
            (params) => hygraphAxiosInstance.post<
            { data: { healthTraits: HealthTraitInfo[] } }
            >(
              hygraphApiEndpoint,
              JSON.stringify({
                query: getHealthTraitInfoListQueryPDFData(params),
              }),
            ),
            (response) => response.data.data.healthTraits.length,
          );

          return chunks.reduce((acc, chunk) => {
            acc.data.data.healthTraits = acc.data
              .data.healthTraits.concat(chunk.data.data.healthTraits);
            return acc;
          }, {
            data: {
              data: {
                healthTraits: [],
              },
            },
          } as AxiosResponse<{ data: { healthTraits: [] } }>);
        },
      getBodyFunctions:
        () => hygraphAxiosInstance.post<
        { data: { healthTraitBodyFunctions: HealthTraitBodyFunction[] } }
        >(
          hygraphApiEndpoint,
          JSON.stringify({
            query: HEALTH_TRAIT_BODY_FUNCTIONS_QUERY,
          }),
        ),
      getHealthTraitResultTypes:
        async () => {
          const result = await hygraphAxiosInstance.post<
          { data: { healthTraitResultTypes: HealthTraitResultType[] } }
          >(
            hygraphApiEndpoint,
            JSON.stringify({
              query: HEALTH_TRAIT_RESULT_TYPES_QUERY,
            }),
          );

          if (!result.data.data.healthTraitResultTypes
            ?.find((type) => type.traitPredictionId === HealthTraitPrediction.UNDETERMINED)
          ) {
            result.data.data.healthTraitResultTypes.push(defaultUndeterminedHealthTraitResultType);
          }

          return result;
        },
      getHealthTraitInheritanceResults:
        async () => {
          const chunks = await loadAllChunks(
            100,
            (params) => hygraphAxiosInstance.post<
            { data: { healthTraitInheritanceResults: HealthTraitInheritanceResult[] } }
            >(
              hygraphApiEndpoint,
              JSON.stringify({
                query: getHealthTraitInheritanceResults(params),
              }),
            ),
            (response) => response.data.data.healthTraitInheritanceResults.length,
          );
          const result = chunks.reduce((acc, chunk) => {
            acc.data.data.healthTraitInheritanceResults = acc.data
              .data.healthTraitInheritanceResults
              .concat(chunk.data.data.healthTraitInheritanceResults);
            return acc;
          }, {
            data: {
              data: {
                healthTraitInheritanceResults: [],
              },
            },
          } as AxiosResponse<{ data: { healthTraitInheritanceResults: [] } }>);

          if (!result.data.data.healthTraitInheritanceResults
            .find((res) => res.inheritance === HealthTraitInheritance.UNDETERMINED)
          ) {
            result.data.data.healthTraitInheritanceResults.push(
              defaultUndeterminedHealthTraitInheritanceResult,
            );
          }

          return result;
        },
      getHealthTraitsForInheritance:
        async () => {
          const chunks = await loadAllChunks(
            100,
            (params) => hygraphAxiosInstance.post<
            { data: { healthTraits: Pick<HealthTraitInfo, 'traitId' | 'affectedBreeds' | 'inheritance'>[] } }
            >(
              hygraphApiEndpoint,
              JSON.stringify({
                query: getHealthTraitsForInheritance(params),
              }),
            ),
            (response) => response.data.data.healthTraits.length,
          );

          return chunks.reduce((acc, chunk) => {
            acc.data.data.healthTraits = acc.data
              .data.healthTraits.concat(chunk.data.data.healthTraits);
            return acc;
          }, {
            data: {
              data: {
                healthTraits: [],
              },
            },
          } as AxiosResponse<{ data: { healthTraits: [] } }>);
        },
    },

    traits: {
      getTraitsResults: (testId: string) => axiosInstance.get<TraitsResults>(
        `/api/tests/${testId}/traits`,
      ),
      getTraitInfo:
        (traitId: string) => hygraphAxiosInstance.post<{ data: { traits: TraitInfo[] } }>(
          hygraphApiEndpoint,
          JSON.stringify({
            query: 'query TraitQuery($traitId:String!){traits(first:1000,where:{traitId:$traitId}){id name geneLocus references{html} traitId traitType image{id url} traitResults{label title predictionId detailText} traitCopies {name title text additionalInformation {text markdown} }}}',
            variables: {
              traitId,
            },
          }),
        ),
      getTraitInfoForPDF:
        (where: {}) => hygraphAxiosInstance.post<{
          data: {
            traits: {
              traitId: string,
              name: string,
              geneLocus: string[],
              traitType: TraitCategory,
              traitResults: { predictionId: string, detailText: string }[]
            }[]
          }
        }>(
          hygraphApiEndpoint,
          JSON.stringify({
            query: 'query Traits($traitsWhere: TraitWhereInput) {traits (where: $traitsWhere, first: 100) {traitId name geneLocus traitType traitResults {predictionId detailText}}}',
            variables: {
              traitsWhere: where,
            },
          }),
        ),
      getTraitList: (traitIds: string[]) => hygraphAxiosInstance.post<{ data: TraitsBasicInfo }>(
        hygraphApiEndpoint,
        JSON.stringify({
          query: 'query TraitsQuery($traitIds:[String]){traits(first:1000,where:{traitId_in:$traitIds}){traitId name traitType image{id url} traitResults{label title predictionId}}}',
          variables: {
            traitIds,
          },
        }),
      ),
      getAllTraitList: () => hygraphAxiosInstance.post<{ data: TraitsBasicInfo }>(
        hygraphApiEndpoint,
        JSON.stringify({
          query: 'query {traits(first:1000){traitId name traitType image{id url} traitResults{label title predictionId}}}',
        }),
      ),
    },

    user: {
      getUserProfile: () => axiosInstance.get<UserProfile>('/api/userpro/profile'),
      generateImageToken: (testId: string) => axiosInstance.get<ImageToken>(`/api/profiles/${testId}/generate-image-token`),
      uploadProfileImg: (namespace: string, mediaId: string, extension: string, token: string, data: any) => mediaUploadAxiosInstance.put(`/v2/stream/namespaces/${namespace}/media/${mediaId}?client=petdna-profile&extension=${extension}&securityToken=${token}`, data),
      setPetDNADisplayName: (petDNADisplayName: string) => axiosInstance.patch(`api/userpro/profile?petDNADisplayName=${petDNADisplayName}`),
      setAllMessagesOn: (allMessagesOn: boolean) => axiosInstance.patch(`api/userpro/profile?disableMessages=${allMessagesOn}`),
      setMeasurementPreference: (measurementPreference: string) => axiosInstance.patch(`/api/userpro/profile?measurementPreference=${measurementPreference}`),
      updateUserProfile: (profile: TUpdateUserProfile) => axiosInstance.patch('/api/userpro/profile', profile),
    },

    page: {
      getRegistrationConsent: () => hygraphAxiosInstance.post(hygraphApiEndpoint, JSON.stringify({
        query: registrationConsentsQuery,
      })),
      getRegistrationMarketingConsent: () => hygraphAxiosInstance.post(
        hygraphApiEndpoint,
        JSON.stringify({
          query: registrationMarketingConsentsQuery,
        }),
      ),
      getPageHome: (
        locale: TSupportedLocales[],
      ) => hygraphAxiosInstance.post<{ data: { pageLiMarketing: IPageHome } }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: PAGE_HOME_QUERY, variables: { locale } }),
      ),
      getPolicy: (policyId: string) => hygraphAxiosInstance.post<{ data: { policy: TPolicyPage } }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: GET_POLICY_QUERY, variables: { policyId } }),
      ),
      getSiteFooter: (variables: ISiteFooterVariables) => hygraphAxiosInstance
        .post<{ data: { siteFooters: ISiteFooter[] } }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: siteFooterQuery, variables }),
      ),
      getSiteHeader: (variables: ISiteHeaderVariables) => hygraphAxiosInstance
        .post<{ data: { siteHeaders: ISiteHeader[] } }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: siteHeaderQuery, variables }),
      ),
      getKitPrices: () => hygraphAxiosInstance.post<{ data: { kitPrices: IKitPrices[] } }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: KIT_PRICES_ONLY_QUERY }),
      ),
      getPageLoggedInDashboard:
        () => hygraphAxiosInstance.post<{ data: { pageLiDashboard: IPageLoggedInDashboard } }>(
          hygraphApiEndpoint,
          JSON.stringify({ query: GET_LOGGED_IN_DASHBOARD_QUERY }),
        ),
      getCookiesDisclosure:
        () => hygraphAxiosInstance.post<{ data: { pageAccount: ICookieDisclosure } }>(
          hygraphApiEndpoint,
          JSON.stringify({ query: GET_COOKIES_DISCLOSURE }),
        ),
      getPagePartnerships:
      (
        locale: TSupportedLocales[],
      ) => hygraphAxiosInstance.post<{ data: { pagePartnership: IPagePartnerships } }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: PAGE_PARTNERSHIPS_QUERY, variables: { locale } }),
      ),
      getSurveyList:
      () => hygraphAxiosInstance.post<{ data: ISurveys }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: SURVEYS_QUERY }),
      ),
      getPageOffer:
        (
          { cid, pageOfferId }: PageOfferVars,
          locale: TSupportedLocales[],
        ) => hygraphAxiosInstance.post<{ data: { pageLiOffer: IPageOffer } }>(
          hygraphApiEndpoint,
          JSON.stringify({
            query: GET_OFFER_QUERY,
            variables: {
              where: {
                ...(!pageOfferId && { couponPrefix: cid }),
                ...(pageOfferId && { id: pageOfferId }),
              },
              locale,
            },
          }),
        ),
      getPageCoBranded:
        (name: string, locale: TSupportedLocales) => hygraphAxiosInstance.post<{
          data: { pageCoBranded: { name: string, sections: TPageCoBrandedSections } }
        }>(
          hygraphApiEndpoint,
          JSON.stringify({ query: PAGE_CO_BRANDED_QUERY, variables: { name, locale: [locale, 'en'] } }),
        ),
      getPromoBanners:
        (
          enabled: boolean,
          locale: TSupportedLocales[],
          forProject: TForProject,
        ) => hygraphAxiosInstance.post<{
          data: { promoBanners: ITopPagePromoBanner[] };
        }>(
          hygraphApiEndpoint,
          JSON.stringify({ query: PROMO_BANNER_QUERY, variables: { enabled, locale, forProject } }),
        ),
      getSectionDashboardPartnership:
        () => hygraphAxiosInstance.post<{
          data: { sectionDashboardPartnerships: ISectionDashboardPartnership[] };
        }>(
          hygraphApiEndpoint,
          JSON.stringify({ query: SECTION_DASHBOARD_PARTNERSHIP_QUERY }),
        ),
      getSectionDashboardCarousel:
        async (locales: TSupportedLocales[]) => {
          const response = await hygraphAxiosInstance.post<{
            data: { sectionDashboardCarousels: ISectionDashboardCarousel[] };
          }>(
            hygraphApiEndpoint,
            JSON.stringify({ query: SECTION_DASHBOARD_CAROUSEL_QUERY }),
          );

          if (response?.data?.data?.sectionDashboardCarousels.length) {
            const section = response.data.data.sectionDashboardCarousels[0];

            section.items = section.items.filter((item) => locales.includes(item.itemLocale));
          }

          return response;
        },
      getUnionPage: ({
        page,
        countryCode,
        omitLocale = false,
        pageSectionQueries,
      }: IUnionPageVars & { countryCode: TSupportedCountryCodes }) => hygraphAxiosInstance.post<{
        data: { unionPages: IUnionPage[] };
      }>(
        hygraphApiEndpoint,
        JSON.stringify({
          query: unionPagesQuery(pageSectionQueries),
          variables: {
            pageId: page,
            countryCode: [countryCode],
            ...(!omitLocale && { locale: getLocalesByCountryCode(countryCode) }),
          },
        }),
      ),
      getRedirectFromPetDNAPage:
      (variables: { type: ERedirectType }) => hygraphAxiosInstance.post<{
        data: { pageRedirectFromPetDna: TPageRedirectFromPetDna }
      }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: PAGE_REDIRECT_FROM_PETDNA_QUERY, variables }),
      ),
      getShoppingCart: (id: string, countryCode: TSupportedCountryCodes) => {
        const variables: IShoppingCartVariables = { id, kitsSaleCountry: countryCode };
        return hygraphAxiosInstance.post<{
          data: { shoppingCart: IShoppingCart }
        }>(
          hygraphApiEndpoint,
          JSON.stringify({ query: shoppingCartQuery, variables }),
        );
      },
      getBiopetUpdateCard: () => hygraphAxiosInstance.post<{
        data: { biopetUpdateCard: IBiopetUpdateCardQuery }
      }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: biopetUpdateCardQuery, variables: biopetUpdateCardQueryVariables }),
      ),
    },

    barcode: {
      validateActivationCode: (activationCode: string) => axiosInstance.get<{ valid: boolean }>(
        `api/bff/validate-activation-code?activationCode=${activationCode}`,
      ),
    },

    faqs: {
      getFaqsSearch:
        (search: string) => hygraphAxiosInstance.post<{ data: { faqs: IFaqSearchResultItem[] } }>(
          hygraphApiEndpoint,
          JSON.stringify({
            query: 'query FAQs($search: String!) { faqs (where: {_search: $search}) { id question externalLink } }',
            variables: {
              search,
            },
          }),
        ),
    },

    survey: {
      getSurveyCard:
        (surveyId: string) => hygraphAxiosInstance.post<{ data: ISurvey }>(
          hygraphApiEndpoint,
          JSON.stringify({
            query: SURVEY_QUERY,
            variables: {
              surveyId,
            },
          }),
        ),
      startSession:
        (
          surveyId: string,
          testGuid: string | string[],
        ) => axiosInstance.post<IStartQualtricsSessionResponse>(
          `api/surveys/${surveyId}/sessions`,
          { testGuid },
        ),
      saveQuestionsAnswers:
        (
          surveyId: string,
          testGuid: string,
          responses: TQualtricsSessionResponse,
        ) => axiosInstance.post<ISaveQualtricsAnswerResponse>(
          `api/surveys/${surveyId}/sessions/${testGuid}`,
          { responses },
        ),
      getUserSessions:
        (surveyId: string[]) => axiosInstance.get<IQualtricsSession[]>('api/surveys/user', {
          params: { surveyId },
        }),
      getUserSurveySessions:
        (surveyId: string) => axiosInstance.get<IQualtricsSession[]>(`api/surveys/user/${surveyId}`),
    },

    socialShare: {
      getSocialShareButtonList: (
        id: string,
      ) => hygraphAxiosInstance.post<{ data: { socialShareButtonList: ISocialShareButtonList } }>(
        hygraphApiEndpoint,
        JSON.stringify({ query: socialShareButtonListQuery, variables: { id } }),
      ),
    },

    hygraph: {
      hygraphApiEndpoint,
      hygraphAxiosInstance,
    },

    popups: {
      getPopupList: (id: string) => hygraphAxiosInstance.post<{ data: { popupList: IPopupList } }>(
        hygraphApiEndpoint,
        JSON.stringify({
          query: getPopupListQuery(id),
        }),
      ),
      getPopups: <T extends TPopupSetting = TPopupSetting>(
        variables: object,
      ) => hygraphAxiosInstance.post<{ data: { popups: IPopup<T>[] } }>(
        hygraphApiEndpoint,
        JSON.stringify({
          query: POPUPS_QUERY,
          variables,
        }),
      ),
    },
  };
};

export default initApiClient;
